import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TypedFormBuilder } from '../../form/TypedFormBuilder';
import { UntypedFormArray, Validators } from '@angular/forms';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { GetMineFullDetailsGQL } from '../../../../../../photographer-panel/src/graphql';
import { DirtyForm, DirtyFormInterface } from '@ui/form/validation/DirtyForm';
import {
  HasValidation,
  HasValidationInterface,
} from '@ui/form/validation/HasValidation';
import { applyMixins } from 'ZalamoLibrary/src/lib/core/classes';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  finalize,
  first,
  switchMap,
  tap,
} from 'rxjs';
import { ValidationHelperService } from '@ui/form/validation/validation-helper.service';
import { ZalamoTooltipService } from '@ui/zalamo-tooltip/zalamo-tooltip.service';

import { OrganizationInvoiceDetailsService } from '@ui/organization-invoice-details/organization-invoice-details.service';
import { CreateZalamoPayAccountGQL } from '@ui/zalamo-pay/graphql';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';

const peselValidator = (control) => {
  const weight = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3];
  let sum = 0;
  const controlNumber = Number(control.value.substring(10, 11));

  for (let i = 0; i < weight.length; i++) {
    sum += Number(control.value.substring(i, i + 1)) * weight[i];
  }
  sum = sum % 10;
  return (10 - sum) % 10 === controlNumber ? null : { peselValidator: true };
};

@Component({
  selector: 'lib-zalamo-pay-register-p24',
  templateUrl: './zalamo-pay-register-p24.component.html',
  styleUrls: ['./zalamo-pay-register-p24.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ZalamoPayRegisterP24Component
  extends AliveState
  implements OnInit, DirtyFormInterface, HasValidationInterface
{
  readonly registerLoading$ = new BehaviorSubject<boolean>(false);
  @ViewChild('button', { static: true }) button: any;
  me = this;
  model = this.form.typedGroup<{
    businessType: string;
    name: string;
    phoneNumber: string;
    bankAccount: string;
    krs: string;
    nip: string;
    regon: string;
    contactPersonName: string;
    contactPersonPhoneNumber: string;
    addressCity: string;
    addressPostCode: string;
    addressStreet: string;
    correspondenceAddressCity: string;
    correspondenceAddressPostCode: string;
    correspondenceAddressStreet: string;
    representatives: {
      name: string;
      pesel: string;
    }[];
  }>({
    businessType: ['', Validators.required],
    name: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    bankAccount: ['', Validators.required],
    krs: [''],
    nip: [
      '',
      [Validators.required, Validators.minLength(10), Validators.maxLength(10)],
    ],
    regon: [
      '',
      [Validators.required, Validators.minLength(9), Validators.maxLength(9)],
    ],
    contactPersonName: ['', Validators.required],
    contactPersonPhoneNumber: ['', Validators.required],
    addressCity: ['', Validators.required],
    addressPostCode: ['', Validators.required],
    addressStreet: ['', Validators.required],
    correspondenceAddressCity: ['', Validators.required],
    correspondenceAddressPostCode: ['', Validators.required],
    correspondenceAddressStreet: ['', Validators.required],
    representatives: new UntypedFormArray([]),
  });

  get representatives() {
    return this.model.controls.representatives as UntypedFormArray;
  }

  constructor(
    private form: TypedFormBuilder,
    private getMineFullDetailsGQL: GetMineFullDetailsGQL,
    private organizationInvoiceDetailsService: OrganizationInvoiceDetailsService,
    public validationService: ValidationHelperService,
    public tooltipService: ZalamoTooltipService,
    private createZalamoPayAccountGQL: CreateZalamoPayAccountGQL,
    private organizationService: OrganizationService,
  ) {
    super();
  }

  takeSnapshotForDirty() {
    return JSON.stringify(this.model.value);
  }

  addRep() {
    this.representatives.push(
      this.form.typedGroup<{ name: string; pesel: string }>({
        name: [``, Validators.required],
        pesel: ['', [Validators.required, peselValidator]],
      }),
    );
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      this.model.controls.businessType.valueChanges.pipe(
        tap((v) => {
          if (Number(v) > 3) {
            this.model.controls.krs.setValidators([Validators.required]);
          } else {
            this.model.controls.krs.setValidators([]);
          }
        }),
      ),
    );
    combineLatest([
      this.organizationService
        .getCurrentOrganization()
        .pipe(
          switchMap((organizationId) =>
            this.getMineFullDetailsGQL.fetch({ variables: { organizationId } }),
          ),
        ),
      this.organizationInvoiceDetailsService.details(),
    ])
      .pipe(first())
      .subscribe(
        ([
          {
            data: { mineAccountDetails },
          },
          { address, city, country, postCode, vatNumber, companyName },
        ]) => {
          console.log(mineAccountDetails);
          const firstDigit = (mineAccountDetails.companyAddress ?? '').match(
            /\d/,
          );
          const streetWithoutComasTrimmed = (
            mineAccountDetails.companyAddress ??
            mineAccountDetails.privateAddress
          )
            ?.slice(0, firstDigit.index)
            .replace(',', '')
            .trim();
          const numbers = (
            mineAccountDetails.companyAddress ??
            mineAccountDetails.privateAddress
          )?.slice(firstDigit.index);
          const [buildingNumber, flatNumber] = numbers
            ? numbers.split('/')
            : ['', ''];
          this.representatives.push(
            this.form.typedGroup<{ name: string; pesel: string }>({
              name: [
                `${mineAccountDetails.firstName} ${mineAccountDetails.lastName}`,
                Validators.required,
              ],
              pesel: ['', [Validators.required, peselValidator]],
            }),
          );
          this.model.patchValue({
            name: companyName ? companyName : mineAccountDetails.companyName,
            addressCity: city ? city : `${mineAccountDetails.companyCity}`,
            nip: vatNumber ? vatNumber : mineAccountDetails.companyVatId,
            addressPostCode: postCode
              ? postCode
              : mineAccountDetails.companyZipCode,
            addressStreet: address ? address : streetWithoutComasTrimmed,
            correspondenceAddressCity: `${mineAccountDetails.privateCity}`,
            correspondenceAddressStreet: streetWithoutComasTrimmed,
            correspondenceAddressPostCode: mineAccountDetails.companyZipCode,
            phoneNumber: mineAccountDetails.phone,
            contactPersonPhoneNumber: mineAccountDetails.phone,
            contactPersonName: `${mineAccountDetails.firstName} ${mineAccountDetails.lastName}`,
            // ...mineAccountDetails,
            // companyFlatNumber: flatNumber ?? '',
            // companyBuildingNumber: buildingNumber ?? '',
            // companyStreet: streetWithoutComasTrimmed,
            // email: mineAccountDetails.email,
            // phone: mineAccountDetails.phone,
            // firstName: mineAccountDetails.firstName,
            // lastName: mineAccountDetails.lastName,
            // buildingNumber,
            // city:
            //   mineAccountDetails.privateCity ?? mineAccountDetails.companyCity,
            // flatNumber,
            // postCode:
            //   mineAccountDetails.companyZipCode ??
            //   mineAccountDetails.privateZipCode,
            // street: streetWithoutComasTrimmed,
          });
        },
      );
  }

  register() {
    if (this.registerLoading$.value) {
      return;
    }

    return new Observable<boolean>((subscriber) => {
      this.model.updateValueAndValidity();
      if (this.validate({ target: this.button.nativeElement }, this.model)) {
        subscriber.next(false);
        this.currentFormValue = this.takeSnapshotForDirty();
        // this.navigationService.saveState = true;
        const current = this.model.getRawValue();
        this.registerLoading$.next(true);
        this.organizationService
          .getCurrentOrganization()
          .pipe(
            switchMap((organizationId) =>
              this.createZalamoPayAccountGQL.mutate({
                variables: {
                  input: this.model.getRawValue(),
                  organizationId,
                },
              }),
            ),
            tap(
              ({
                data: {
                  createZalamoPayAccount: { status, link },
                },
              }) => {
                if (status) {
                  location.href = link;
                }
              },
            ),
            first(),
            finalize(() => this.registerLoading$.next(false)),
          )
          .subscribe();
        // const update: PrivateSessionUpdateInput = {
        //   ...removeTypename(current),
        // };
        // this.PrivateSessionUpdate.mutate(
        //   {
        //     privateSessionId: this._privateSessionId,
        //     update,
        //   },
        //   {
        //     optimisticResponse: {
        //       __typename: 'Mutation',
        //       // @ts-ignore
        //       updatePrivateSession: {
        //         __typename: 'PrivateSession',
        //         ...merge(this._session, update),
        //       },
        //     },
        //   }
        // )
        //   .toPromise()
        //   .then(() => {
        //     this.exitWarnService.remove(
        //       `privateSession-${this._privateSessionId}`
        //     );
        //     this.navigationService.saveState = false;
        //   });
      } else {
        subscriber.next(false);
      }
    }).subscribe();
  }
}

export interface ZalamoPayRegisterP24Component
  extends DirtyFormInterface, HasValidationInterface {}

applyMixins(ZalamoPayRegisterP24Component, [DirtyForm, HasValidation]);
